'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    CompanyCertificate,
    deleteCertificate,
    FiscalPreferences,
    getCertificates,
    getFiscalPreferences,
    updateFiscalPreferences,
    uploadCertificate,
} from '@/lib/api/fiscal/fiscal-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import { MdArrowBack, MdDelete, MdUpload } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const FiscalSettingsPage = () => {
    const navigate = useNavigate()

    const [certificates, setCertificates] = useState<CompanyCertificate[]>([])
    const [loading, setLoading] = useState(true)
    const [uploading, setUploading] = useState(false)
    const [certFile, setCertFile] = useState<File | null>(null)
    const [certPassword, setCertPassword] = useState('')
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [preferences, setPreferences] = useState<FiscalPreferences>({
        autoGenerateInvoices: false,
        autoSendEmail: false,
    })
    const [savingPrefs, setSavingPrefs] = useState(false)

    useEffect(() => {
        loadData()
    }, [])

    const loadData = async () => {
        setLoading(true)
        const [certsRes, prefsRes] = await Promise.all([
            getCertificates(),
            getFiscalPreferences(),
        ])
        if (certsRes.success) setCertificates(certsRes.result || [])
        if (prefsRes.success && prefsRes.result) setPreferences(prefsRes.result)
        setLoading(false)
    }

    const handleTogglePreference = async (key: keyof FiscalPreferences, value: boolean) => {
        const updates: Partial<FiscalPreferences> = { [key]: value }

        // Ao ativar geração automática, ativa envio por email junto
        if (key === 'autoGenerateInvoices' && value) {
            updates.autoSendEmail = true
        }
        // Ao desativar geração automática, desativa envio por email
        if (key === 'autoGenerateInvoices' && !value) {
            updates.autoSendEmail = false
        }

        const updated = { ...preferences, ...updates }
        setPreferences(updated)
        setSavingPrefs(true)
        const res = await updateFiscalPreferences(updates)
        if (!res.success) {
            setPreferences((prev) => ({ ...prev, ...Object.fromEntries(Object.keys(updates).map(k => [k, !updates[k as keyof FiscalPreferences]])) }))
            toast.error('Erro ao salvar preferência')
        }
        setSavingPrefs(false)
    }

    const handleUpload = async () => {
        if (!certFile) {
            toast.error('Selecione o arquivo do certificado')
            return
        }
        if (!certPassword) {
            toast.error('Informe a senha do certificado')
            return
        }
        setUploading(true)
        const response = await uploadCertificate(certFile, certPassword)
        if (response.success) {
            toast.success('Certificado enviado e empresa registrada no serviço fiscal')
            setCertFile(null)
            setCertPassword('')
            loadData()
        } else {
            toast.error(response.error)
        }
        setUploading(false)
    }

    const handleDelete = async () => {
        if (!deleteId) return
        setDeleteLoading(true)
        const response = await deleteCertificate(deleteId)
        if (response.success) {
            toast.success('Certificado removido')
            loadData()
        } else {
            toast.error(response.error)
        }
        setDeleteLoading(false)
        setDeleteId(null)
    }

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <span className="loading loading-spinner loading-lg"></span>
            </div>
        )
    }

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
                <button className="btn btn-ghost btn-sm" onClick={() => navigate('/fiscal/invoices')}>
                    <MdArrowBack size={20} />
                </button>
                <h1 className="text-2xl font-bold">Configurações Fiscais</h1>
            </div>

            {/* Certificado A1 */}
            <div className="card bg-base-100 shadow-sm border border-base-200 mb-6">
                <div className="card-body">
                    <h2 className="card-title text-lg mb-4">Certificado Digital A1</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Arquivo .pfx / .p12</span>
                            </label>
                            <input
                                type="file"
                                className="file-input file-input-bordered w-full"
                                accept=".pfx,.p12"
                                onChange={(e) => setCertFile(e.target.files?.[0] || null)}
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Senha do certificado</span>
                            </label>
                            <div className="flex gap-2">
                                <input
                                    type="password"
                                    className="input input-bordered flex-1"
                                    value={certPassword}
                                    onChange={(e) => setCertPassword(e.target.value)}
                                />
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleUpload}
                                    disabled={uploading}
                                >
                                    {uploading ? (
                                        <span className="loading loading-spinner loading-sm"></span>
                                    ) : (
                                        <MdUpload size={18} />
                                    )}
                                    Enviar
                                </button>
                            </div>
                        </div>
                    </div>

                    {certificates.length > 0 && (
                        <div className="mb-4">
                            {certificates.map((cert) => (
                                <div key={cert.id} className="flex items-center justify-between p-3 bg-base-200 rounded-lg mb-2">
                                    <div>
                                        <p className="font-medium text-sm">{cert.subject}</p>
                                        <p className="text-xs text-base-content/60">
                                            CNPJ: {cert.cnpj} | Válido até: {format(new Date(cert.validUntil), 'dd/MM/yyyy', { locale: ptBR })}
                                        </p>
                                    </div>
                                    <button
                                        className="btn btn-ghost btn-xs text-error"
                                        onClick={() => setDeleteId(cert.id)}
                                    >
                                        <MdDelete size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}


                </div>
            </div>

            {/* Automação */}
            <div className="card bg-base-100 shadow-sm border border-base-200 mb-6">
                <div className="card-body">
                    <h2 className="card-title text-lg mb-4">Automação</h2>

                    <div className="form-control">
                        <label className="label cursor-pointer justify-start gap-4">
                            <input
                                type="checkbox"
                                className="toggle toggle-secondary"
                                checked={preferences.autoGenerateInvoices}
                                onChange={(e) =>
                                    handleTogglePreference('autoGenerateInvoices', e.target.checked)
                                }
                                disabled={savingPrefs}
                            />
                            <div>
                                <span className="label-text font-medium">
                                    Gerar notas fiscais automaticamente
                                </span>
                                <p className="text-xs text-base-content/60 mt-0.5">
                                    Ao concluir uma venda de serviço ou produto, a nota fiscal será gerada automaticamente
                                </p>
                            </div>
                        </label>
                    </div>

                    <div className="divider my-1"></div>

                    <div className="form-control">
                        <label className="label cursor-pointer justify-start gap-4">
                            <input
                                type="checkbox"
                                className="toggle toggle-secondary"
                                checked={preferences.autoSendEmail}
                                onChange={(e) =>
                                    handleTogglePreference('autoSendEmail', e.target.checked)
                                }
                                disabled={savingPrefs || !preferences.autoGenerateInvoices}
                            />
                            <div>
                                <span className={`label-text font-medium ${!preferences.autoGenerateInvoices ? 'text-base-content/40' : ''}`}>
                                    Enviar nota fiscal por e-mail
                                </span>
                                <p className="text-xs text-base-content/60 mt-0.5">
                                    Após a emissão, a nota fiscal será enviada automaticamente para o e-mail do cliente
                                </p>
                            </div>
                        </label>
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={deleteId !== null}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Remover certificado"
                message="Tem certeza de que deseja remover este certificado?"
                loading={deleteLoading}
                confirmButtonText="Remover"
                confirmButtonClass="btn btn-error"
            />
        </div>
    )
}

export default FiscalSettingsPage
